import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { ThreadMessage } from "@/api/types";
import { Badge, Button } from "@/components/ui";

type Props = { brandId: string; customerId: string; appointmentId?: string | null; compact?: boolean; canSend?: boolean };

const tone = (s: string) => s === "delivered" || s === "seen" || s === "clicked" ? "green" : s === "failed" || s === "undelivered" || s === "expired" ? "red" : s === "received" ? "blue" : "gray";

/** One customer's Viber/SMS conversation with a composer. Marks inbound messages read when opened. */
export function MessageThread({ brandId, customerId, appointmentId, compact, canSend = true }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ["thread", brandId, customerId], queryFn: () => api<ThreadMessage[]>(`/app/threads/${customerId}/messages?brand_id=${brandId}`), refetchInterval: 20000 });
  useEffect(() => {
    if (q.data?.some((m) => m.direction === "in" && !m.read_by_staff_at)) {
      api(`/app/threads/${customerId}/read?brand_id=${brandId}`, { method: "POST" }).then(() => { qc.invalidateQueries({ queryKey: ["threads"] }); qc.invalidateQueries({ queryKey: ["unread-count"] }); }).catch(() => undefined);
    }
    bottom.current?.scrollIntoView({ block: "end" });
  }, [q.data, brandId, customerId, qc]);
  const send = useMutation({
    mutationFn: () => api<ThreadMessage>(`/app/threads/${customerId}/messages`, { method: "POST", body: JSON.stringify({ brand_id: brandId, body: text, appointment_id: appointmentId ?? null }) }),
    onSuccess: () => { setText(""); setError(null); q.refetch(); qc.invalidateQueries({ queryKey: ["threads"] }); },
    onError: (e) => setError(e instanceof ApiError ? t(`msg.err.${(() => { try { return JSON.parse(e.detail).reason; } catch { return e.detail; } })()}`, { defaultValue: e.detail }) : t("app.error")),
  });
  return (
    <div className="flex flex-col text-sm">
      <div className={`space-y-2 overflow-auto rounded-lg bg-gray-50 p-2 ${compact ? "max-h-56" : "max-h-[55vh]"}`}>
        {q.data?.length === 0 && <p className="text-xs text-gray-500">{t("msg.empty")}</p>}
        {q.data?.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 ${m.direction === "out" ? "bg-brand/10" : "bg-white border"}`}>
              <div className="whitespace-pre-wrap break-words">{m.body}</div>
              {m.media_url && <a className="text-xs underline" href={m.media_url} target="_blank" rel="noreferrer">{t("msg.attachment")}</a>}
              <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                <span>{new Date(m.created_at).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" })}</span>
                <span>· {m.channel}</span>
                {m.kind !== "manual" && m.kind !== "reply" && <span>· {t(`msg.kind.${m.kind}`, { defaultValue: m.kind })}</span>}
                <Badge tone={tone(m.status)}>{t(`msg.status.${m.status}`, { defaultValue: m.status })}</Badge>
                {m.last_error && m.status !== "sent" && m.status !== "delivered" && <span className="text-red-600" title={m.last_error}>⚠</span>}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      {canSend && (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate(); }} className="mt-2 flex gap-2">
          <textarea className="w-full rounded-lg border px-3 py-1.5 text-sm" rows={compact ? 2 : 3} placeholder={t("msg.placeholder")} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && text.trim()) send.mutate(); }} />
          <Button disabled={!text.trim() || send.isPending}>{t("msg.send")}</Button>
        </form>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
